import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Component,
  Input,
  Output,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  EventEmitter,
} from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { map, Observable, startWith, Subscription } from 'rxjs';
import { ValidationErrorService } from 'src/app/core/services/error/validation-error/validation-error.service';
import { SelectorData } from 'src/app/utils/selectorDataType';

@Component({
  selector: 'app-search-select',
  templateUrl: './search-select.component.html',
  styleUrls: ['./search-select.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Represents a searchable select field
 */
export class SearchSelectComponent implements OnInit, OnDestroy {
  @Input() label!: string;
  @Input() placeholder!: string;
  @Input() disabled?: boolean = false;
  @Input() control!: FormControl;
  @Input() options?: SelectorData[];
  @Output() selectionChange = new EventEmitter<string>();
  filteredOptions!: Observable<SelectorData[]>;
  private _required: boolean = false;
  private subsink = new Subscription();
  errorMessage = '';

  /**
   * @constructor
   */
  constructor(private validationErrorService: ValidationErrorService) {}

  /**
   * on init
   */
  ngOnInit() {
    if (this.disabled) {
      this.control.disable();
    }

    this.setErrorMessage(this.control);
    const subscription = this.control.statusChanges.subscribe(() => {
      this.setErrorMessage(this.control);
    });
    this.subsink.add(subscription);
    this.filteredOptions = this.control.valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value || ''))
    );
  }

  /**
   * Filters list based on input value
   * @param {string | SelectorData} value
   * @return {SelectorData[]}
   */
  private _filter(value: string | SelectorData): SelectorData[] {
    const filterValue =
      typeof value === 'string'
        ? value.toLowerCase()
        : value.label.toLowerCase();

    if (!this.options) {
      return [];
    }
    return this.options.filter((option) =>
      option.label.toLowerCase().includes(filterValue)
    );
  }

  /**
   * clean up subscriptions
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * If this select is required or not
   * @return {boolean}
   */
  @Input()
  get required(): boolean {
    return this._required;
  }

  /**
   * Sets this select as required
   * @param {any} value
   */
  set required(value: any) {
    this._required = coerceBooleanProperty(value);
  }

  /**
   * Sets error message of this input
   * @param {AbstractControl} abstractControl
   */
  setErrorMessage(abstractControl: AbstractControl) {
    this.validationErrorService
      .translateError(abstractControl)
      .then((errorMessage) => {
        this.errorMessage = errorMessage;
      });
  }

  /**
   * Emits selection change event
   */
  changeSelect() {
    this.selectionChange.emit(this.control.value.value);
  }

  /**
   * Function that maps selector data label to display when a value is selected.
   * @param {SelectorData | undefined} option
   * @return {string}
   */
  displaySelectorDataLabel(option: SelectorData | undefined): string {
    return option ? option.label : '';
  }
}
